import type { StatIntelLine } from '@/hooks/useStats';
import { birthdayTouchpoint, formatSavePct, formatToi } from '@/lib/formatters';
import type { FlagStatus, FlagType } from '@/lib/tokens';

export type DraftVariant = 'balanced' | 'warm' | 'direct';
export type DraftOptions = { variant?: DraftVariant; revision?: number };
export type DraftScenario =
  | 'birthday'
  | 'dnp'
  | 'goalie'
  | 'production'
  | 'giveaways'
  | 'low-minutes'
  | 'no-shot-toi'
  | 'workload'
  | 'detail'
  | 'shots'
  | 'default';

export type Touchpoint = {
  id: string;
  line: StatIntelLine;
  label: string;
  meta: string;
  priority: number;
};

const TOUCHPOINT_LIMIT = 8;

export function buildDraftText(line: StatIntelLine, options: DraftOptions = {}): string {
  const variant = options.variant ?? variantForLine(line);
  const revision = options.revision ?? 0;
  const name = displayName(line);
  const firstName = line.firstName || name.split(' ')[0] || name;
  const context = draftContext(line);
  const birthday = birthdayTouchpoint(line.dob);
  const voice = houseVoice[variant];

  if (birthday) {
    return birthday.daysUntil === 0
      ? voice.birthdayToday(firstName, revision)
      : voice.birthdayUpcoming(firstName, birthday.when, revision);
  }

  if (line.isDnp === 1) {
    return voice.dnp(firstName, context, revision);
  }

  if (isGoalie(line)) {
    const opener =
      line.saves != null && line.shotsAgainst != null && line.shotsAgainst >= 30
        ? 'Looked like you saw plenty of traffic'
        : line.decision === 'W'
          ? 'Nice job getting the result'
          : 'Saw the line from your start';
    return voice.goalie(firstName, opener, context, line.decision, revision);
  }

  const points = line.points ?? 0;
  const shots = line.shots ?? 0;
  const hits = line.hits ?? 0;
  const blocked = line.blocked ?? 0;
  const takeaways = line.takeaways ?? 0;
  const lowMinutes = flagStatusFor(line, 'low-minutes') !== 'ok';
  const noShots = flagStatusFor(line, 'no-shot-toi') !== 'ok';
  const giveawayWatch = flagStatusFor(line, 'giveaway-watch') !== 'ok';

  if (points > 0) {
    return voice.production(firstName, context, line.goals ?? 0, line.assists ?? 0, revision);
  }

  if (giveawayWatch) {
    return voice.giveaways(firstName, context, revision);
  }

  if (lowMinutes) {
    return voice.lowMinutes(firstName, context, revision);
  }

  if (noShots) {
    return voice.noShots(firstName, context, revision);
  }

  if ((line.toiSeconds ?? 0) >= 1_200) {
    return voice.workload(firstName, context, revision);
  }

  if (hits + blocked + takeaways >= 3) {
    return voice.detail(firstName, context, revision);
  }

  if (shots >= 3) {
    return voice.shots(firstName, context, revision);
  }

  return voice.defaultLine(firstName, context, revision);
}

export function scenarioForLine(line: StatIntelLine): DraftScenario {
  if (birthdayTouchpoint(line.dob)) return 'birthday';
  if (line.isDnp === 1) return 'dnp';
  if (isGoalie(line)) return 'goalie';
  if ((line.points ?? 0) > 0) return 'production';
  if (flagStatusFor(line, 'giveaway-watch') !== 'ok') return 'giveaways';
  if (flagStatusFor(line, 'low-minutes') !== 'ok') return 'low-minutes';
  if (flagStatusFor(line, 'no-shot-toi') !== 'ok') return 'no-shot-toi';
  if ((line.toiSeconds ?? 0) >= 1_200) return 'workload';
  if ((line.hits ?? 0) + (line.blocked ?? 0) + (line.takeaways ?? 0) >= 3) return 'detail';
  if ((line.shots ?? 0) >= 3) return 'shots';
  return 'default';
}

export function adaptTrainingExample(line: StatIntelLine, exampleText: string): string {
  const firstName = line.firstName || displayName(line).split(' ')[0] || displayName(line);
  const withoutSpecificNumbers = exampleText
    .replace(/\b\d+\s*-\s*\d+\s*(W|L|OTL|SOL)?\b/gi, '')
    .replace(/\b\d+\s*(G|A|P|SOG|HIT|BLK|GVA|TKA)\b/gi, '')
    .replace(/\b\d{1,2}:\d{2}\s*TOI\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  const addressed = withoutSpecificNumbers.replace(/^(hey|hi)\s+[A-Z][a-z]+,?/i, `Hey ${firstName},`);
  const opener = addressed || buildDraftText(line);
  return `${opener}${/[.!?]$/.test(opener) ? '' : '.'}`.replace(/\s+/g, ' ').trim();
}

type DraftVoice = {
  birthdayToday: (firstName: string, revision: number) => string;
  birthdayUpcoming: (firstName: string, when: string, revision: number) => string;
  dnp: (firstName: string, context: string, revision: number) => string;
  goalie: (firstName: string, opener: string, context: string, decision: string | null, revision: number) => string;
  production: (firstName: string, context: string, goals: number, assists: number, revision: number) => string;
  giveaways: (firstName: string, context: string, revision: number) => string;
  lowMinutes: (firstName: string, context: string, revision: number) => string;
  noShots: (firstName: string, context: string, revision: number) => string;
  workload: (firstName: string, context: string, revision: number) => string;
  detail: (firstName: string, context: string, revision: number) => string;
  shots: (firstName: string, context: string, revision: number) => string;
  defaultLine: (firstName: string, context: string, revision: number) => string;
};

function createVoice(variant: DraftVariant): DraftVoice {
  const templates = voiceTemplates[variant];
  return {
    birthdayToday: (firstName, revision) => fill(pick(templates.birthdayToday, revision), { firstName }),
    birthdayUpcoming: (firstName, when, revision) => fill(pick(templates.birthdayUpcoming, revision), { firstName, when }),
    dnp: (firstName, context, revision) => fill(pick(templates.dnp, revision), { firstName, context }),
    goalie: (firstName, opener, context, decision, revision) =>
      fill(pick(decision === 'W' ? templates.goalieWin : templates.goalie, revision), { firstName, opener, context }),
    production: (firstName, context, goals, assists, revision) => {
      const set = goals > 0 ? templates.goal : assists >= 2 ? templates.multiAssist : templates.production;
      return fill(pick(set, revision), { firstName, context });
    },
    giveaways: (firstName, context, revision) => fill(pick(templates.giveaways, revision), { firstName, context }),
    lowMinutes: (firstName, context, revision) => fill(pick(templates.lowMinutes, revision), { firstName, context }),
    noShots: (firstName, context, revision) => fill(pick(templates.noShots, revision), { firstName, context }),
    workload: (firstName, context, revision) => fill(pick(templates.workload, revision), { firstName, context }),
    detail: (firstName, context, revision) => fill(pick(templates.detail, revision), { firstName, context }),
    shots: (firstName, context, revision) => fill(pick(templates.shots, revision), { firstName, context }),
    defaultLine: (firstName, context, revision) => fill(pick(templates.defaultLine, revision), { firstName, context }),
  };
}

type TemplateKey =
  | 'birthdayToday'
  | 'birthdayUpcoming'
  | 'dnp'
  | 'goalieWin'
  | 'goalie'
  | 'goal'
  | 'multiAssist'
  | 'production'
  | 'giveaways'
  | 'lowMinutes'
  | 'noShots'
  | 'workload'
  | 'detail'
  | 'shots'
  | 'defaultLine';

const voiceTemplates: Record<DraftVariant, Record<TemplateKey, string[]>> = {
  balanced: {
    birthdayToday: [
      'Happy birthday {firstName}! Hope you have a great day. Appreciate the trust always.',
      'Happy birthday {firstName}. Hope today is a good one for you and the family.',
      'Happy birthday {firstName}! Enjoy the day and know we appreciate you.',
    ],
    birthdayUpcoming: [
      'Hey {firstName}, saw your birthday is coming up {when}. Hope you and the family get a good one.',
      'Hey {firstName}, birthday coming up {when}. Wanted to get ahead of it and wish you a good one.',
      'Hey {firstName}, saw the birthday coming up {when}. Hope it is a great week for you.',
    ],
    dnp: [
      "Hey {firstName}, saw you're not in the lineup {context}. Staying on top of it on our end and rooting for you to be back in soon.",
      'Hey {firstName}, saw you were out {context}. We are keeping an eye on it and hoping you are back in rhythm soon.',
      'Hey {firstName}, noticed you were not in {context}. Stay ready and know we are tracking it.',
    ],
    goalieWin: [
      'Nice job getting the result {context}, {firstName}. Good stuff buddy, keep it going.',
      'Good job closing that one out {context}, {firstName}. Keep stacking those starts.',
      'Nice work {context}, {firstName}. Keep building off that and stay sharp.',
    ],
    goalie: [
      'Hey {firstName}, {opener} {context}. Strong outing against a good team, nothing to hang your head about there.',
      'Hey {firstName}, {opener} {context}. Stay with that compete level and keep giving yourself a chance.',
      'Hey {firstName}, {opener} {context}. We are watching the trend and like how you are battling.',
    ],
    goal: [
      'Huge goal {context}! Love that you were out there in that situation. Way to take advantage. Keep it going {firstName}.',
      'Big finish {context}, {firstName}. Keep getting to those spots and making it count.',
      'Great to see you finish {context}, {firstName}. Keep pushing and build off it.',
    ],
    multiAssist: [
      'Really nice playmaking {context}. Great dishes. Great stuff {firstName}.',
      'Loved the way you were moving pucks {context}, {firstName}. Keep driving the play.',
      'Good job creating for others {context}, {firstName}. Keep trusting that part of your game.',
    ],
    production: [
      'Great to see you on the sheet {context}. Keep building buddy.',
      'Good to see you involved {context}, {firstName}. Keep pushing it forward.',
      'Nice to see the offense come through {context}, {firstName}. Keep stacking it.',
    ],
    giveaways: [
      "Hey {firstName}, don't let a couple tough touches {context} become the story. Keep playing with speed and clean up the details next one.",
      'Hey {firstName}, clean up the puck details {context} and keep playing fast. Do not let that define the night.',
      'Hey {firstName}, a few details to tighten {context}, but stay aggressive and keep your pace.',
    ],
    lowMinutes: [
      'Hey {firstName}, stay ready. If the opportunity is a little lighter {context}, make it hard for them to keep you out of the bigger spots.',
      'Hey {firstName}, lighter usage {context}. Stay prepared and make the next opportunity count.',
      'Hey {firstName}, if the looks are limited {context}, keep your habits sharp and be ready for the next one.',
    ],
    noShots: [
      "Hey {firstName}, get those feet moving and look to attack more {context}. When you're getting inside, you're a problem.",
      'Hey {firstName}, look to get inside more {context}. When you are on your toes, good things follow.',
      'Hey {firstName}, keep pushing to create more looks {context}. Attack first and make them handle your pace.',
    ],
    workload: [
      'Big responsibility for you {context}. Staff clearly trusts you in those spots. Keep it going {firstName}.',
      'Staff leaned on you {context}, {firstName}. That trust matters, keep earning it.',
      'You carried real responsibility {context}, {firstName}. Keep showing you can handle those minutes.',
    ],
    detail: [
      "Quiet on the score sheet {context}, but I liked the details in your game. That's winning hockey. Keep leaning into that part.",
      'Even when it is quiet offensively {context}, the detail work matters. Keep doing the little things that help teams win.',
      'Good detail night {context}, {firstName}. Keep bringing that edge even when the points are not there.',
    ],
    shots: [
      "Liked how much you were looking to attack {context}. That's a good sign. Keep being a volume guy {firstName}.",
      'Good attacking mindset {context}, {firstName}. Keep getting pucks through and staying around it.',
      'Liked the shot mentality {context}, {firstName}. Keep putting yourself around chances.',
    ],
    defaultLine: [
      'Keep working {context}. Positive steps and keep building, buddy.',
      'Keep stacking good habits {context}, {firstName}. We are staying on top of it.',
      'Stay with it {context}, {firstName}. Keep building the right way.',
    ],
  },
  warm: {
    birthdayToday: [
      'Happy birthday {firstName}! Hope you get a good day with the people around you. Appreciate you always.',
      'Happy birthday {firstName}. Hope today gives you a little time to enjoy it with family.',
      'Happy birthday {firstName}! Enjoy the day, you deserve a good one.',
    ],
    birthdayUpcoming: [
      'Hey {firstName}, saw your birthday is coming up {when}. Wanted to get ahead of it and wish you a great one.',
      'Hey {firstName}, saw the birthday coming up {when}. Hope you have a great one with the family.',
      'Hey {firstName}, birthday week coming up {when}. Wanted to send a note early.',
    ],
    dnp: [
      'Hey {firstName}, saw you were out {context}. We are paying attention over here and hoping you are back in a good rhythm soon.',
      'Hey {firstName}, noticed you were out {context}. Stay patient and ready, we are watching it with you.',
      'Hey {firstName}, saw the lineup {context}. Hope you are feeling good and staying ready for the next chance.',
    ],
    goalieWin: [
      'Hey {firstName}, nice job getting the result {context}. Love seeing you compete through it.',
      'Great job {context}, {firstName}. Keep trusting your game and stacking these.',
      'Good win {context}, {firstName}. Keep building confidence off that.',
    ],
    goalie: [
      'Hey {firstName}, {opener} {context}. Love seeing you compete through that and we are watching it closely.',
      'Hey {firstName}, {opener} {context}. Stay with that battle, it matters.',
      'Hey {firstName}, {opener} {context}. Keep trusting your work and staying with it.',
    ],
    goal: [
      'Hey {firstName}, loved seeing you finish {context}. Keep trusting your game and building off it.',
      'Great to see you get rewarded {context}, {firstName}. Keep leaning into those moments.',
      'Nice finish {context}, {firstName}. Keep bringing that confidence.',
    ],
    multiAssist: [
      'Hey {firstName}, nice to see the playmaking show up {context}. Keep trusting that feel.',
      'Good job creating {context}, {firstName}. Keep making people around you better.',
      'Loved the puck movement {context}, {firstName}. Keep building off that.',
    ],
    production: [
      'Hey {firstName}, nice to see you involved offensively {context}. Keep trusting your game and building off it.',
      'Hey {firstName}, good to see you get rewarded {context}. Keep stacking those moments.',
      'Hey {firstName}, liked seeing your game show up offensively {context}. Keep going.',
    ],
    giveaways: [
      'Hey {firstName}, do not let a couple tough touches {context} take away from the way you are competing. Clean it up and keep playing with pace.',
      'Hey {firstName}, some cleanup details {context}, but keep your confidence and stay aggressive.',
      'Hey {firstName}, tighten the puck details {context} and keep trusting your feet.',
    ],
    lowMinutes: [
      'Hey {firstName}, stay ready if the opportunity is a little lighter {context}. Be ready to grab the next bigger look.',
      'Hey {firstName}, lighter opportunity {context}, but keep your habits right and be ready.',
      'Hey {firstName}, if the role is smaller {context}, keep preparing like the next look is yours.',
    ],
    noShots: [
      'Hey {firstName}, keep looking to get inside and attack {context}. When you are on your toes, good things follow.',
      'Hey {firstName}, keep pushing to create more looks {context}. Your game is better when you are attacking.',
      'Hey {firstName}, stay on the front foot {context}. Get inside and make them deal with you.',
    ],
    workload: [
      'Big responsibility for you {context}, {firstName}. Staff is leaning on you, and that trust matters. Keep earning it.',
      'Hey {firstName}, they leaned on you {context}. That says something. Keep building on it.',
      'Hey {firstName}, good responsibility {context}. Keep showing them they can trust you there.',
    ],
    detail: [
      'Hey {firstName}, even when it is quiet offensively {context}, the detail work matters. Keep doing the little things that help teams win.',
      'Hey {firstName}, liked the detail in your game {context}. Keep stacking those habits.',
      'Hey {firstName}, good detail work {context}. That stuff travels even when the offense is quiet.',
    ],
    shots: [
      'Hey {firstName}, liked that you were looking to attack {context}. Keep putting yourself around chances.',
      'Hey {firstName}, liked the way you were hunting looks {context}. Keep that mindset.',
      'Hey {firstName}, good sign seeing you around chances {context}. Keep pressing.',
    ],
    defaultLine: [
      'Hey {firstName}, keep stacking good habits. We are watching the details and staying on top of it.',
      'Hey {firstName}, keep building. The little details add up and we are watching them.',
      'Hey {firstName}, stay with the process. Keep doing the things that travel.',
    ],
  },
  direct: {
    birthdayToday: [
      'Happy birthday {firstName}. Hope it is a great day for you and the family.',
      'Happy birthday {firstName}. Enjoy the day.',
      'Happy birthday {firstName}. Wishing you a great one.',
    ],
    birthdayUpcoming: [
      'Hey {firstName}, birthday coming up {when}. Wanted to wish you a good one early.',
      'Hey {firstName}, saw the birthday coming up {when}. Hope it is a good one.',
      'Hey {firstName}, getting ahead of your birthday {when}. Hope you enjoy it.',
    ],
    dnp: [
      'Hey {firstName}, saw you were out {context}. We are tracking it and will stay close to the next lineup.',
      'Hey {firstName}, saw the lineup {context}. Stay ready and we will keep watching it.',
      'Hey {firstName}, noticed you were out {context}. Keep preparing and be ready for the next one.',
    ],
    goalieWin: [
      'Good result {context}, {firstName}. Keep building off that and stay sharp.',
      'Strong result {context}, {firstName}. Keep stacking starts.',
      'Good job closing it out {context}, {firstName}. Stay with it.',
    ],
    goalie: [
      'Hey {firstName}, {opener} {context}. Stay with it and keep giving yourself a chance every night.',
      'Hey {firstName}, {opener} {context}. Keep battling and keep the standard.',
      'Hey {firstName}, {opener} {context}. Stay sharp and keep competing.',
    ],
    goal: [
      'Nice job finishing {context}, {firstName}. Keep getting to those spots and making it count.',
      'Good finish {context}, {firstName}. Keep finding those areas.',
      'Big goal {context}, {firstName}. Keep putting yourself in position.',
    ],
    multiAssist: [
      'Good playmaking {context}, {firstName}. Keep moving pucks and driving the play.',
      'Good job creating offense {context}, {firstName}. Keep pushing pace.',
      'Nice job making plays {context}, {firstName}. Keep trusting it.',
    ],
    production: [
      'Good to see you involved {context}, {firstName}. Keep pushing it forward.',
      'Good offensive involvement {context}, {firstName}. Keep building.',
      'Nice to see you get on it {context}, {firstName}. Keep going.',
    ],
    giveaways: [
      'Hey {firstName}, clean up the puck details {context} and keep playing fast. Do not let that become the story.',
      'Hey {firstName}, tighten the details {context}. Keep your pace and move on.',
      'Hey {firstName}, clean that part up {context} and keep attacking.',
    ],
    lowMinutes: [
      'Hey {firstName}, lighter usage {context}. Stay ready and make the next opportunity hard to ignore.',
      'Hey {firstName}, smaller role {context}. Be ready to grab the next look.',
      'Hey {firstName}, limited opportunity {context}. Keep your habits sharp.',
    ],
    noShots: [
      'Hey {firstName}, look to attack more {context}. Get inside and put them under pressure.',
      'Hey {firstName}, need more attack {context}. Get to the hard areas.',
      'Hey {firstName}, keep pushing for looks {context}. Make them defend you.',
    ],
    workload: [
      'Staff trusted you with responsibility {context}, {firstName}. Keep showing you can handle it.',
      'Good responsibility {context}, {firstName}. Keep earning those spots.',
      'They leaned on you {context}, {firstName}. Keep proving you can handle it.',
    ],
    detail: [
      'Quiet offensively {context}, but the detail work matters. Keep leaning into that.',
      'Good detail work {context}, {firstName}. Keep bringing that part.',
      'Details were there {context}, {firstName}. Keep doing that consistently.',
    ],
    shots: [
      'Good attacking mindset {context}, {firstName}. Keep getting pucks through and staying around it.',
      'Good shot mentality {context}, {firstName}. Keep pressing.',
      'Keep attacking {context}, {firstName}. That mindset matters.',
    ],
    defaultLine: [
      'Keep building {context}, {firstName}. Stay with the habits and the rest follows.',
      'Stay with it {context}, {firstName}. Keep stacking the right details.',
      'Keep working {context}, {firstName}. Build off the good habits.',
    ],
  },
};

function pick(values: string[], revision: number): string {
  return values[Math.abs(revision) % values.length] ?? values[0] ?? '';
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '');
}

const houseVoice: Record<DraftVariant, DraftVoice> = {
  balanced: createVoice('balanced'),
  warm: createVoice('warm'),
  direct: createVoice('direct'),
};

function variantForLine(line: StatIntelLine): DraftVariant {
  const key = `${line.statLineId}:${line.playerId}`;
  const hash = [...key].reduce((total, char) => total + char.charCodeAt(0), 0);
  return (['balanced', 'warm', 'direct'] as DraftVariant[])[hash % 3] ?? 'balanced';
}

export function buildTouchpoints(lines: StatIntelLine[], limit = TOUCHPOINT_LIMIT): Touchpoint[] {
  return lines
    .flatMap((line) => touchpointsForLine(line))
    .sort((a, b) => b.priority - a.priority || displayName(a.line).localeCompare(displayName(b.line)))
    .slice(0, limit);
}

export function touchpointsForLine(line: StatIntelLine): Touchpoint[] {
  const touchpoints: Touchpoint[] = [];
  const name = displayName(line);
  const birthday = birthdayTouchpoint(line.dob);
  const context = gameContext(line);

  if (birthday) {
    touchpoints.push({
      id: `${line.statLineId}:birthday`,
      line,
      label: birthday.label,
      meta: birthday.daysUntil === 0 ? `Birthday today for ${name}` : `${name} birthday ${birthday.when}`,
      priority: 100 - birthday.daysUntil,
    });
  }

  if (line.isDnp === 1) {
    touchpoints.push({
      id: `${line.statLineId}:dnp`,
      line,
      label: 'DNP check-in',
      meta: `Out ${context}`,
      priority: 70,
    });
    return touchpoints;
  }

  if (isGoalie(line)) {
    if ((line.shotsAgainst ?? 0) >= 30 || line.decision === 'W') {
      touchpoints.push({
        id: `${line.statLineId}:goalie`,
        line,
        label: line.decision === 'W' ? 'Goalie result' : 'High-volume start',
        meta: goalieSummary(line),
        priority: line.decision === 'W' ? 82 : 76,
      });
    }
    return touchpoints;
  }

  const detailCount = (line.hits ?? 0) + (line.blocked ?? 0) + (line.takeaways ?? 0);
  if ((line.points ?? 0) > 0) {
    touchpoints.push({
      id: `${line.statLineId}:production`,
      line,
      label: 'Production',
      meta: `${compactSkaterLine(line)} ${context}`,
      priority: 90 + (line.points ?? 0),
    });
  }

  if ((line.toiSeconds ?? 0) >= 1_200) {
    touchpoints.push({
      id: `${line.statLineId}:workload`,
      line,
      label: 'Heavy workload',
      meta: `${formatToi(line.toiSeconds)} TOI ${context}`,
      priority: 86,
    });
  }

  if (detailCount >= 3) {
    touchpoints.push({
      id: `${line.statLineId}:detail`,
      line,
      label: 'Detail line',
      meta: skaterDetail(line),
      priority: 78,
    });
  }

  for (const flag of ['giveaway-watch', 'low-minutes', 'no-shot-toi'] as FlagType[]) {
    const status = flagStatusFor(line, flag);
    if (status === 'ok') continue;
    touchpoints.push({
      id: `${line.statLineId}:${flag}`,
      line,
      label: flagLabel(flag),
      meta: flagMeta(line, flag),
      priority: status === 'critical' ? 84 : 74,
    });
  }

  return touchpoints;
}

export function displayName(line: Pick<StatIntelLine, 'displayName' | 'firstName' | 'lastName'>): string {
  return line.displayName || [line.firstName, line.lastName].filter(Boolean).join(' ');
}

export function flagStatusFor(line: Pick<StatIntelLine, 'flags'>, type: FlagType): FlagStatus {
  return (line.flags.find((flag) => flag.flagType === type)?.status as FlagStatus | undefined) ?? 'ok';
}

function isGoalie(line: StatIntelLine): boolean {
  return line.position === 'G' || line.decision != null;
}

function gameContext(line: StatIntelLine): string {
  const opponent = line.opponent ? `${line.isHome === 0 ? 'at' : 'against'} ${line.opponent}` : 'last night';
  const score =
    line.teamScore != null && line.opponentScore != null
      ? `${line.teamScore}-${line.opponentScore}${line.result ? ` ${line.result}` : ''}`
      : line.result ?? null;
  return score ? `${opponent} (${score})` : opponent;
}

function draftContext(line: StatIntelLine): string {
  if (line.isDnp === 1) return 'tonight';
  return 'last night';
}

function skaterDetail(line: StatIntelLine): string {
  return [
    `${num(line.goals, '0')}G`,
    `${num(line.assists, '0')}A`,
    `${num(line.points, '0')}P`,
    `${num(line.shots, '0')} SOG`,
    line.hits != null ? `${line.hits} HIT` : null,
    line.blocked != null ? `${line.blocked} BLK` : null,
    line.giveaways != null ? `${line.giveaways} GVA` : null,
    line.takeaways != null ? `${line.takeaways} TKA` : null,
    formatToi(line.toiSeconds) !== '—' ? `${formatToi(line.toiSeconds)} TOI` : null,
  ]
    .filter(Boolean)
    .join(', ');
}

function compactSkaterLine(line: StatIntelLine): string {
  return [
    `${num(line.goals, '0')}G`,
    `${num(line.assists, '0')}A`,
    `${num(line.points, '0')}P`,
    line.shots != null ? `${line.shots} SOG` : null,
    formatToi(line.toiSeconds) !== '—' ? `${formatToi(line.toiSeconds)} TOI` : null,
  ]
    .filter(Boolean)
    .join(', ');
}

function goalieSummary(line: StatIntelLine): string {
  return [
    line.decision,
    line.saves != null && line.shotsAgainst != null ? `${line.saves}/${line.shotsAgainst}` : null,
    formatSavePct(line.savePct) !== '—' ? `${formatSavePct(line.savePct)} SV%` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function flagLabel(flag: FlagType): string {
  if (flag === 'giveaway-watch') return 'Giveaway watch';
  if (flag === 'low-minutes') return 'Low shift';
  if (flag === 'no-shot-toi') return 'No-shot TOI';
  return 'IR watch';
}

function flagMeta(line: StatIntelLine, flag: FlagType): string {
  if (flag === 'giveaway-watch') return `${num(line.giveaways, '0')} GVA in feed`;
  if (flag === 'low-minutes') return `${formatToi(line.toiSeconds)} TOI usage note`;
  if (flag === 'no-shot-toi') return `${formatToi(line.toiSeconds)} TOI, 0 SOG`;
  return 'Check injury context';
}

function num(value: number | null | undefined, fallback = '—'): string {
  if (value == null) return fallback;
  return String(value);
}
